// Apolo IDE — pure helpers for the @apolo GitHub-mention executor (E2-T05).
//
// I/O-free. The Electron remote-runner (useRemoteRunner) consumes a dispatch
// whose context.source === 'github_mention' and runs a clone→agent→PR→comment
// flow. These helpers build the deterministic bits of that flow (the
// token-bearing clone URL, the branch slug, the PR title/body, the comment) so
// they can be unit-tested without touching git/shell/fetch.
//
// The installation token is a SECRET: it rides the dispatch payload server→Mac
// and must never be logged or sent to a frontend route. buildCloneUrl is the
// ONLY place it appears, and callers must keep that URL out of logs/events.

const DEFAULT_TMP_ROOT = '/tmp/apolo-gh';
const SLUG_MAX = 40;

/** Is this dispatch a github-mention dispatch? */
export function isGithubMentionDispatch(dispatch) {
  return dispatch?.context?.source === 'github_mention';
}

/**
 * Slugify an instruction into a branch-safe fragment: lowercase, non-alnum →
 * hyphens, collapsed, trimmed, capped at ~40 chars. Empty/garbage → 'task'.
 */
export function slugify(text, max = SLUG_MAX) {
  const base = String(text ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip diacritics
    .replace(/[^a-z0-9]+/g, '-') // non-alnum → hyphen
    .replace(/^-+|-+$/g, ''); // trim hyphens
  if (!base) return 'task';
  let out = base.slice(0, max).replace(/-+$/g, '');
  return out || 'task';
}

/**
 * Branch name for a mention dispatch: `apolo/issue-{n}-{slug}`.
 * Always matches the git BRANCH_RE (/^[A-Za-z0-9._/-]+$/) the IPC enforces.
 */
export function buildBranchName({ issueNumber, instruction } = {}) {
  const n = Number.isFinite(Number(issueNumber)) ? Number(issueNumber) : 0;
  return `apolo/issue-${n}-${slugify(instruction)}`;
}

/**
 * Token-bearing HTTPS clone URL. SECRET — never log the result.
 *   https://x-access-token:{token}@github.com/{repoFullName}.git
 */
export function buildCloneUrl({ installationToken, repoFullName } = {}) {
  if (!installationToken) throw new Error('[github-mention] installationToken required');
  if (!repoFullName) throw new Error('[github-mention] repoFullName required');
  return `https://x-access-token:${installationToken}@github.com/${repoFullName}.git`;
}

/**
 * Deterministic working dir for a dispatch: `{tmpRoot}/{dispatchId}`. Same id →
 * same dir (idempotent re-runs), distinct ids never collide.
 */
export function buildWorkingDir({ dispatchId, tmpRoot = DEFAULT_TMP_ROOT } = {}) {
  const safe = String(dispatchId || 'unknown').replace(/[^A-Za-z0-9._-]/g, '_');
  return `${tmpRoot.replace(/\/+$/, '')}/${safe}`;
}

/** Split "owner/repo" → { owner, repo }. Returns nulls when malformed. */
export function splitRepoFullName(repoFullName) {
  const parts = String(repoFullName || '').split('/');
  if (parts.length < 2 || !parts[0] || !parts[1]) return { owner: null, repo: null };
  return { owner: parts[0], repo: parts[1] };
}

/** PR title from the instruction (single line, capped). */
export function buildPrTitle({ instruction, issueNumber } = {}) {
  const oneLine = String(instruction || '').replace(/\s+/g, ' ').trim();
  const capped = oneLine.length > 72 ? `${oneLine.slice(0, 69)}...` : oneLine;
  return capped || `Apolo: address #${issueNumber ?? ''}`.trim();
}

/**
 * PR body: links the originating issue (Closes #n), restates the instruction,
 * and includes the agent's summary. Signed so a human knows it's the bot.
 */
export function buildPrBody({ issueNumber, instruction, summary, author } = {}) {
  const lines = [];
  if (Number.isFinite(Number(issueNumber))) lines.push(`Closes #${Number(issueNumber)}`);
  lines.push('');
  if (author) lines.push(`Requested by @${author}:`);
  if (instruction) lines.push(`> ${String(instruction).replace(/\n/g, '\n> ')}`);
  if (summary) {
    lines.push('');
    lines.push('## What changed');
    lines.push(String(summary).trim());
  }
  lines.push('');
  lines.push('---');
  lines.push('🤖 Opened by [@apolo](https://github.com/apps/apolo) — an automated agent.');
  return lines.join('\n');
}

/**
 * The comment Apolo posts back on the issue/PR thread. MUST carry the bot
 * marker so the webhook's bot-guard ignores it (anti-loop). Two shapes:
 *   - opened a PR  → link + summary
 *   - no changes   → "nada a fazer"
 */
export function buildResultComment({ prUrl, summary, noChanges } = {}) {
  const BOT_SIGNATURE = '\n\n<sub>🤖 posted by @apolo[bot]</sub>';
  if (noChanges || !prUrl) {
    return `Não encontrei nenhuma mudança a fazer para essa solicitação — nada a commitar.${BOT_SIGNATURE}`;
  }
  const parts = [`✅ Abri um PR com as mudanças: ${prUrl}`];
  if (summary && String(summary).trim()) {
    parts.push('');
    parts.push(String(summary).trim());
  }
  return parts.join('\n') + BOT_SIGNATURE;
}

// ── GitHub REST I/O (renderer-side mirror of apolo-api createPullRequest) ─────
// The install token is LOCAL-ONLY — these run on the Mac via the runtime fetch,
// never through a frontend route. Mirrors the request shape of
// apolo-api/src/lib/githubApi.js::createPullRequest so the two stay consistent.

const GITHUB_API = 'https://api.github.com';

function ghHeaders(token) {
  return {
    Authorization: `token ${token}`,
    Accept: 'application/vnd.github+json',
    'X-GitHub-Api-Version': '2022-11-28',
    'User-Agent': 'apolo-ide',
    'Content-Type': 'application/json',
  };
}

/** Open a PR via the install token. Returns { ok, pull_request } | { ok:false, error }. */
export async function createPullRequest({
  token,
  owner,
  repo,
  base,
  head,
  title,
  body,
  fetchImpl = fetch,
} = {}) {
  if (!token) return { ok: false, error: 'token required' };
  if (!owner || !repo || !head || !title) return { ok: false, error: 'owner/repo/head/title required' };
  const res = await fetchImpl(`${GITHUB_API}/repos/${owner}/${repo}/pulls`, {
    method: 'POST',
    headers: ghHeaders(token),
    body: JSON.stringify({ title, head, base: base || 'main', body: body || '', draft: false }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, status: res.status, error: data.message || `create PR failed: ${res.status}` };
  }
  return {
    ok: true,
    pull_request: { number: data.number, html_url: data.html_url, state: data.state },
  };
}

/** Post a comment on the originating issue/PR thread. Best-effort. */
export async function postIssueComment({
  token,
  repoFullName,
  issueNumber,
  body,
  fetchImpl = fetch,
} = {}) {
  if (!token || !repoFullName || !issueNumber) return { ok: false, error: 'token/repo/issue required' };
  const res = await fetchImpl(
    `${GITHUB_API}/repos/${repoFullName}/issues/${issueNumber}/comments`,
    { method: 'POST', headers: ghHeaders(token), body: JSON.stringify({ body }) },
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, status: res.status, error: data.message || `comment failed: ${res.status}` };
  }
  return { ok: true };
}

/** React ✅ on the originating comment (so the user sees Apolo finished). */
export async function reactDone({ token, repoFullName, commentId, fetchImpl = fetch } = {}) {
  if (!token || !repoFullName || !commentId) return { ok: false };
  try {
    await fetchImpl(
      `${GITHUB_API}/repos/${repoFullName}/issues/comments/${commentId}/reactions`,
      { method: 'POST', headers: ghHeaders(token), body: JSON.stringify({ content: '+1' }) },
    );
    return { ok: true };
  } catch {
    return { ok: false };
  }
}
